import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'

export interface MyTeamMember {
  uuid: string
  email: string
  firstName: string | null
  lastName: string | null
  status: string
  bu?: string | null
  metadata?: {
    odoo16Id?: number | null
    odoo16PartnerId?: number | null
    luccaId?: number | null
    [key: string]: unknown
  } | null
}

/**
 * Source unique pour les infos de "mon équipe" depuis boostcore.
 * `members` contient uniquement les subordonnés ; `all` inclut le manager au début.
 *
 * `fetch()` appelle toujours `/users/me/team?strict=true` pour éviter le shortcut
 * SUPER_ADMIN côté serveur qui renverrait l'ensemble des users (262 vs 4). Aucun
 * écran front ne veut ce comportement : "mon équipe" = hiérarchie réelle.
 */
export const useMyTeamStore = defineStore('myTeam', () => {
  const suiteStore = useSuiteStore()
  const boostCoreStore = useBoostCorePermissionStore()
  const { user: boostCoreUser } = storeToRefs(boostCoreStore)

  const members = ref<MyTeamMember[]>([])
  const isLoaded = ref(false)
  const isLoading = ref(false)

  const me = computed<MyTeamMember | null>(() => {
    const u = boostCoreUser.value
    if (!u) return null
    return {
      uuid: u.uuid,
      email: u.email,
      firstName: u.firstName,
      lastName: u.lastName,
      status: u.status,
      metadata: u.metadata ?? null,
    }
  })

  const all = computed<MyTeamMember[]>(() => {
    const list: MyTeamMember[] = []
    if (me.value) list.push(me.value)
    list.push(...members.value)
    return list
  })

  async function fetch(): Promise<MyTeamMember[]> {
    isLoading.value = true
    try {
      const res = await suiteStore.suiteFetch('users/me/team?strict=true', {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        const json = await res.json()
        const list = Array.isArray(json?.data)
          ? json.data
          : json?.data
            ? [json.data]
            : []
        members.value = list
        isLoaded.value = true
        return members.value
      }
    } catch (e) {
      console.error('Erreur récupération équipe boostcore', e)
    } finally {
      isLoading.value = false
    }
    return []
  }

  function memberLabel(m: MyTeamMember): string {
    const name = [m.firstName, m.lastName].filter(Boolean).join(' ')
    return name || m.email
  }

  /** IDs Odoo (res.users.id) de l'équipe, sans nulls. */
  const odooIds = computed<number[]>(() =>
    all.value
      .map((m) => m.metadata?.odoo16Id ?? null)
      .filter((id): id is number => typeof id === 'number'),
  )

  /** IDs Odoo res.partner de l'équipe, sans nulls. */
  const partnerIds = computed<number[]>(() =>
    all.value
      .map((m) => m.metadata?.odoo16PartnerId ?? null)
      .filter((id): id is number => typeof id === 'number'),
  )

  /** Emails de l'équipe, sans entrées vides. */
  const emails = computed<string[]>(() =>
    all.value.map((m) => m.email).filter((e): e is string => !!e),
  )

  function findByEmail(email: string): MyTeamMember | undefined {
    return all.value.find((m) => m.email === email)
  }

  function findByOdooId(odooId: number): MyTeamMember | undefined {
    return all.value.find((m) => m.metadata?.odoo16Id === odooId)
  }

  function reset() {
    members.value = []
    isLoaded.value = false
    isLoading.value = false
  }

  return {
    members,
    me,
    all,
    isLoaded,
    isLoading,
    odooIds,
    partnerIds,
    emails,
    fetch,
    memberLabel,
    findByEmail,
    findByOdooId,
    reset,
  }
})
